import { useEffect, useState } from 'react';

import { startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formatByLocale, WeekDays } from 'utils/date';

import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import { DatePicker } from 'components/shared';
import dayjs from 'dayjs';
import 'dayjs/locale/en'; // Import English locale
import { Typography } from '@mui/material';

const RecurrenceRuleStyles = styled.div`
  .divider + .divider {
    padding-top: 10px;
  }

  .heading {
    padding: 6px 0;
  }

  .inline-input {
    width: 50px;
    margin: 0 10px;

    input {
      text-align: center;
    }
  }

  .week-day {
    width: 30px;
    height: 30px;
    border-radius: 50%;

    &.selected {
      color: white;
    }

    .MuiChip-label {
      padding: 0;
    }

    & + .week-day {
      margin-left: 5px;
    }
  }

  .start-date-container {
    padding-bottom: 20px;

    .date-picker-wrapper {
      display: inline-flex;
    }
  }
`;

enum Type {
  WEEK = 'WEEK',
  MONTH = 'MONTH'
}

export type RecurrenceRuleType = {
  start: Date;
  type: Type.WEEK | Type.MONTH;
  interval: number;
  weekDays?: string[];
  dayOfMonth?: number;
  setPosition?: number;
};

type Props = {
  onRecurrenceRuleChange: (recurrenceRule: RecurrenceRuleType | null) => void;
  recurrenceRule?: RecurrenceRuleType | null;
};

export default function RecurrenceRule({
  onRecurrenceRuleChange,
  recurrenceRule
}: Props) {
  const { t } = useTranslation('common');
  const getDefaultStart = () => startOfDay(new Date());
  const [start, setStart] = useState<Date | null>(
    recurrenceRule?.start || getDefaultStart()
  );
  const [type, setType] = useState(recurrenceRule?.type || Type.MONTH);
  const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>(
    recurrenceRule?.weekDays ||
      (recurrenceRule?.start
        ? [WeekDays[recurrenceRule.start.getDay()]]
        : [dayjs().locale('en').format('dddd').toUpperCase()])
  );

  const getWeekOfMonth = (date: Date) => {
    const offsetDate = date.getDate() - 1;
    return Math.floor(offsetDate / 7) + 1;
  };

  const getNegativeWeekOfMonth = (date: Date) => {
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const offsetDate = lastDayOfMonth.getDate() - date.getDate();
    return (Math.floor(offsetDate / 7) + 1) * -1;
  };

  const getDefaultMonthOption = (
    recurrenceRule: RecurrenceRuleType | null | undefined
  ) => {
    if (recurrenceRule) {
      return recurrenceRule.dayOfMonth
        ? 'dayOfMonth'
        : recurrenceRule?.setPosition &&
          recurrenceRule.setPosition < 0 &&
          start &&
          getNegativeWeekOfMonth(start) >= -2
        ? 'setPositionNegative'
        : 'setPosition';
    }
    return getWeekOfMonth(getDefaultStart()) < 5
      ? 'setPosition'
      : 'setPositionNegative';
  };

  const getDefaultDayOfMonth = (
    recurrenceRule: RecurrenceRuleType | null | undefined
  ): number | undefined =>
    recurrenceRule && getDefaultMonthOption(recurrenceRule) === 'dayOfMonth'
      ? recurrenceRule.start.getDate()
      : undefined;

  const getDefaultSetPosition = (
    recurrenceRule: RecurrenceRuleType | null | undefined
  ): number | undefined => {
    switch (getDefaultMonthOption(recurrenceRule)) {
      case 'setPosition':
        return getWeekOfMonth(
          recurrenceRule ? recurrenceRule.start : getDefaultStart()
        );
      case 'setPositionNegative':
        return getNegativeWeekOfMonth(
          recurrenceRule ? recurrenceRule.start : getDefaultStart()
        );
      default:
        return undefined;
    }
  };

  const [interval, setInterval] = useState(recurrenceRule?.interval || 1);
  const [selectedMonthOption, setSelectedMonthOption] = useState<string>(
    getDefaultMonthOption(recurrenceRule)
  );
  const [dayOfMonth, setDayOfMonth] = useState<number | undefined>(
    getDefaultDayOfMonth(recurrenceRule)
  );
  const [setPosition, setSetPosition] = useState<number | undefined>(
    getDefaultSetPosition(recurrenceRule)
  );

  useEffect(() => {
    let recurrenceRule: RecurrenceRuleType | null = null;
    if (start) {
      recurrenceRule = {
        start,
        type,
        interval
      };
      if (type === Type.WEEK) {
        recurrenceRule.weekDays = selectedWeekDays;
      }
      if (type === Type.MONTH) {
        recurrenceRule.dayOfMonth = dayOfMonth;
        recurrenceRule.setPosition = setPosition;
        if (setPosition) {
          recurrenceRule.weekDays = [WeekDays[start?.getDay()]];
        }
      }
    }
    onRecurrenceRuleChange(recurrenceRule);
  }, [
    start,
    type,
    interval,
    selectedWeekDays,
    dayOfMonth,
    setPosition,
    onRecurrenceRuleChange
  ]);

  const onStartDateChanged = (pickerDate: Date | null) => {
    if (pickerDate) {
      if (type === Type.MONTH) {
        switch (selectedMonthOption) {
          case 'dayOfMonth':
            setDayOfMonth(pickerDate.getDate());
            break;
          case 'setPosition': {
            const weekOfMonth = getWeekOfMonth(pickerDate);
            if (weekOfMonth < 5) {
              setSetPosition(weekOfMonth);
            } else {
              setSetPosition(getNegativeWeekOfMonth(pickerDate));
              setSelectedMonthOption('setPositionNegative');
            }
            break;
          }
          case 'setPositionNegative': {
            const negativeWeekOfMonth = getNegativeWeekOfMonth(pickerDate);
            if (negativeWeekOfMonth > -3) {
              setSetPosition(negativeWeekOfMonth);
            } else {
              setSetPosition(getWeekOfMonth(pickerDate));
              setSelectedMonthOption('setPosition');
            }
            break;
          }
          default:
            break;
        }
      } else {
        setSelectedWeekDays([WeekDays[pickerDate.getDay()]]);
        setDayOfMonth(undefined);
        setSetPosition(undefined);
      }
    }
    setStart(pickerDate);
  };

  const onSetDayOfMonth = (dayOfMonth: number) => {
    setSetPosition(undefined);
    setDayOfMonth(dayOfMonth);
    setSelectedMonthOption('dayOfMonth');
  };

  const onSetPosition = (setPosition: number) => {
    setDayOfMonth(undefined);
    setSetPosition(setPosition);
    setSelectedMonthOption(
      setPosition > 0 ? 'setPosition' : 'setPositionNegative'
    );
  };

  const weekOfMonth = start ? getWeekOfMonth(start) : 0;
  const negativeWeekOfMonth = start ? getNegativeWeekOfMonth(start) : 0;

  return (
    <RecurrenceRuleStyles className="recurrence-rule">
      <div className="start-date-container">
        <h4>{t('startDate')}</h4>
        <DatePicker date={start} onChange={onStartDateChanged} showInput />
      </div>
      <h4>{t('recurrenceRule')}</h4>
      <div className="d-flex v-center divider">
        <div className="heading">{t('repeatEvery')}</div>
        <TextField
          value={interval}
          type="number"
          variant="standard"
          className="inline-input"
          onChange={(e) => setInterval(parseInt(e.target.value, 10))}
          inputProps={{ min: 1 }}
        />
        <Select value={type} onChange={(e) => setType(e.target.value as Type)}>
          <MenuItem key={Type.WEEK.toString()} value={Type.WEEK}>
            {t(`week${interval === 1 ? '' : 's'}`)}
          </MenuItem>
          <MenuItem key={Type.MONTH.toString()} value={Type.MONTH}>
            {t(`month${interval === 1 ? '' : 's'}`)}
          </MenuItem>
        </Select>
      </div>
      {type === Type.WEEK && (
        <div className="divider">
          <div className="heading">
            {t('repeatOn')}{' '}
            {selectedWeekDays.length > 0 && (
              <Typography variant="body2" display="inline">
                {t(`planningCycle.${selectedWeekDays[0].toLowerCase()}`)}
              </Typography>
            )}
          </div>
        </div>
      )}
      {type === Type.MONTH && start && (
        <div className="divider">
          <Select value={selectedMonthOption}>
            {weekOfMonth < 5 && (
              <MenuItem
                key="setPosition"
                value="setPosition"
                onClick={() => onSetPosition(weekOfMonth)}
              >
                {t(`onThe${weekOfMonth}`)}{' '}
                {formatByLocale(start, 'EEEE').toLowerCase()}
              </MenuItem>
            )}
            {negativeWeekOfMonth > -3 && (
              <MenuItem
                key="setPositionNegative"
                value="setPositionNegative"
                onClick={() => onSetPosition(negativeWeekOfMonth)}
              >
                {t(`onThe${negativeWeekOfMonth}`)}{' '}
                {formatByLocale(start, 'EEEE').toLowerCase()}
              </MenuItem>
            )}
            <MenuItem
              key="dayOfMonth"
              value="dayOfMonth"
              onClick={() => onSetDayOfMonth(start.getDate())}
            >
              {t('onDay')} {start.getDate()}
            </MenuItem>
          </Select>
        </div>
      )}
    </RecurrenceRuleStyles>
  );
}
